import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../services/apiService';
import { ChatSearchResult } from '../../types';

interface Props {
  onSelectResult: (conversationId: string, messageId: string) => void;
}

const DEBOUNCE_MS = 350;
const MIN_QUERY_LEN = 2;

function formatDate(iso: string): string {
  const d = new Date(iso.replace(' ', 'T'));
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) +
         ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

// Mise en évidence simple des mots de la requête dans le contenu
function highlight(content: string, query: string): React.ReactNode[] {
  const terms = query.trim().split(/\s+/).filter(t => t.length >= 2);
  if (terms.length === 0) return [content];
  const regex = new RegExp(`(${terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'ig');
  return content.split(regex).map((part, i) =>
    regex.test(part)
      ? <mark key={i} className="bg-cyan-200 dark:bg-cyan-400/30 text-cyan-900 dark:text-cyan-100 rounded-sm px-0.5">{part}</mark>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

const ChatSearchBar: React.FC<Props> = ({ onSelectResult }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ChatSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < MIN_QUERY_LEN) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.chatSearch(q, 20);
        setResults(res);
        setError(null);
      } catch (err: any) {
        setError(err?.message || 'Erreur de recherche');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query]);

  const handleClick = (r: ChatSearchResult) => {
    onSelectResult(r.conversation_id, r.id);
    setShowDropdown(false);
    setQuery('');
  };

  const showResults = showDropdown && query.trim().length >= MIN_QUERY_LEN;

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={e => { setQuery(e.target.value); setShowDropdown(true); }}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)} // delay pour permettre le click
        placeholder="🔍 Rechercher dans les messages…"
        className="orion-settings-input w-full rounded-xl px-3 py-2 text-xs"
      />

      {showResults && (
        <div className="absolute top-full left-0 right-0 mt-1 max-h-[400px] overflow-y-auto z-30 rounded-xl border border-slate-200 dark:border-[rgba(100,140,210,0.3)] bg-white dark:bg-[rgba(15,30,60,0.97)] backdrop-blur shadow-2xl">
          {loading && (
            <div className="p-3 text-center text-xs text-slate-500 dark:text-[#546a96]">Recherche…</div>
          )}
          {!loading && error && (
            <div className="p-3 text-center text-xs text-rose-600 dark:text-rose-300">{error}</div>
          )}
          {!loading && !error && results.length === 0 && (
            <div className="p-3 text-center text-xs text-slate-500 dark:text-[#546a96]">Aucun résultat.</div>
          )}
          {!loading && !error && results.map(r => (
            <button
              key={r.id}
              type="button"
              onClick={() => handleClick(r)}
              className="w-full text-left px-3 py-2 border-b border-slate-100 dark:border-[rgba(100,140,210,0.1)] hover:bg-slate-100 dark:hover:bg-[rgba(100,140,210,0.1)] transition-colors"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] font-bold text-cyan-600 dark:text-cyan-300 truncate">
                  {r.conv_type === 'group' ? `# ${r.conv_name || 'Groupe'}` : `💬 ${r.sender_name}`}
                </span>
                <span className="text-[9px] text-slate-400 dark:text-[#546a96] flex-shrink-0">{formatDate(r.created_at)}</span>
              </div>
              <div className="text-xs text-slate-900 dark:text-[#e0eaff] line-clamp-2">
                <span className="text-slate-500 dark:text-[#7f93bc]">{r.sender_name} : </span>
                {highlight(r.content, query)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatSearchBar;
